import axios from "axios";
import apiBase from "../utils/apiBase";
// helper function for creating headers object
const getAuthHeaders = () => ({
  "auth-token": localStorage.getItem("auth"),
  "user-id": localStorage.getItem("userId"),
  database: localStorage.getItem("db"),
  // Which of the views this person holds for that database they chose to read
  // it through (utils/readingContext.js). Absent means "all of them, merged",
  // which is what the server did before anyone could choose. The server uses
  // it only where they already hold it, so it can only ever narrow - a stale
  // or forged id falls back to the merge.
  view: localStorage.getItem("view") || "",
});

export const sendChatMessage = async ({
  message,
  assistantId,
  threadId,
  database,
}) => {
  const authToken = localStorage.getItem("auth");
  const userId = localStorage.getItem("userId");

  if (!authToken || !userId) {
    throw new Error("Missing authentication");
  }

  const res = await axios.post(
    `${apiBase()}/api/chat`,
    {
      message,
      assistantId,
      threadId: threadId || null,
      database,
    },
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      }
    }
  );

  return res.data;
};

export const searchProcessByName = async (name) => {
  if (!name) return [];
  return await axios
    .get(`/processes/name/${name}`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.processes;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getProcessOccurences = async (guid) => {
  if (!guid) return [];
  return await axios
    .get(`/processes/getOccurences/${guid}`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.content;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

// Same list as getProcessOccurences, plus what the search looked at: whether
// it read every candidate diagram or gave up on some (too large, past the
// cap, timed out) - `?detail=1` on the same route. A reader of the Occurrences
// tab needs this to say "some large diagrams weren't searched" instead of
// implying a short list is a complete one.
export const getProcessOccurencesDetail = async (guid) => {
  const empty = { items: [], searched: null, truncated: false };
  if (!guid) return empty;
  return await axios
    .get(`/processes/getOccurences/${guid}?detail=1`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return {
          items: response?.data?.content || [],
          searched: response?.data?.searched || null,
          truncated: Boolean(response?.data?.truncated),
        };
      }
      return empty;
    })
    .catch((err) => {
      console.error(err);
      return empty;
    });
};

export const getProcessRelationships = async (guid) => {
  if (!guid) return [];
  return await axios
    .get(`/processes/getRelationships/${guid}`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.content;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getProcessSVG = async (guid) => {
  if (!guid) return [];
  return await axios
    .get(`/processes/getSVG/${guid}`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getProcessBPMN = async (guid) => {
  if (!guid) return [];
  return await axios
    .get(`/processes/getBPMN/${guid}`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

// The parts of an object, for its structure map. `from` is the object the
// reader came from: a map follows a relationship both ways, so the object one
// level up can be among this one's parts, and the way back must never be
// offered as a step forward - the server leaves it out.
export const getProcessStructure = async (guid, from) => {
  if (!guid) return [];
  const query = from && from !== guid ? `?from=${encodeURIComponent(from)}` : "";
  return await axios
    .get(`/processes/structure/${encodeURIComponent(guid)}${query}`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getProcessLibraryTree = async () => {
  return await axios
    .get(`/processes/getProcessLibraryTree`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.content;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getE2ETree = async () => {
  return await axios
    .get(`/processes/getE2ETree`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.content;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getBlueprintsTree = async () => {
  return await axios
    .get(`/processes/getBlueprintsTree`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.content;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getWorkPackagesTree = async () => {
  return await axios
    .get(`/processes/getWorkPackagesTree`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.content;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getEdges = async (guid) => {
  if (!guid) return [];
  return await axios
    .get(`/processes/getEdges/${guid}`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.content;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

// `relation` is the relationship type the reader opened and `direction` which
// way it runs from the object they are standing on. A group is one of each, so
// both have to travel: the same relationship both ways round is two groups.
export const getNodes = async (guid, relation, direction) => {
  if (!(guid && relation)) return [];
  return await axios
    .post(
      `/processes/getNodes/${guid}`,
      { relation, direction },
      {
        headers: {
          ...getAuthHeaders(),
        },
      }
    )
    .then((response) => {
      if (response.status === 200) {
        return response?.data?.content;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const authenticate = async (email, password) => {
  if (!(email && password))
    return {
      success: false,
      msg: "Please enter email and password",
    };

  return await axios(
    "/neoPublic/authenticate",
    {
      method: "POST",
      data: {
        email: email.toLowerCase().trim(), //always convert email into lower case
        password,
      },
    },
    { withCredentials: true }
  )
    .then((response) => {
      if (response.data?.success) {
        localStorage.setItem("auth", response.data.content?.token); //saving token in local storage
        return response.data;
      }
      return {
        success: false,
        msg: "Internal Server Error",
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Something went wrong. Please try again.",
      };
    });
};

// Tells the server a person signed out on purpose, so it reaches the security
// log (POST /neoPublic/sign-out -> auth.signout). Call it BEFORE clearing
// localStorage, since it sends the current token. Fire-and-forget: it returns
// nothing, never throws and never waits, so signing out can't fail because of
// it. `fetch` with `keepalive` because the callers reload or leave the page
// straight away, which would cancel an ordinary request. Not for the
// session-expired path: there the token is already refused.
export const recordSignOut = () => {
  try {
    const token = localStorage.getItem("auth");
    if (!token || typeof fetch !== "function") return;
    fetch(`${apiBase()}/neoPublic/sign-out`, {
      method: "POST",
      headers: { "auth-token": token },
      keepalive: true,
    }).catch(() => {});
  } catch (err) {
    // Recording is best effort; the sign-out itself goes ahead regardless.
  }
};

export const getProfile = async (token) => {
  if (!token)
    return {
      success: false,
      msg: "no token available",
    };

  return await axios(
    "/neoPublic/profile",
    {
      method: "GET",
      headers: {
        "auth-token": token,
      },
    },
    { withCredentials: true }
  )
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        // status and reason tell a server problem (keep the session) from a
        // rejected account (sign out); no response at all is a network problem.
        status: err?.response?.status,
        reason: err?.response?.data?.reason,
        network: !err?.response,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const signInLinkedin = async (data) => {
  if (!data)
    return {
      success: false,
      msg: "Please enter valid data",
    };

  return await axios("neoPublic/signInLinkedin", {
    method: "POST",
    data: {
      token: data.code,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const requestProlongation = async (email) => {
  return await axios(`/neoPublic/request-prolongation`, {
    method: "POST",
    data: {
      email,
    },
  })
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })

    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const requestAccount = async (data) => {
  return await axios(
    "/neoPublic/request-account",
    {
      method: "POST",
      params: { regid: window.location.search.split("=")[1] }, //extract id from string and send query parameter (for analysis)
      data: {
        email: data.email.toLowerCase(), //always convert email into lower case,
        company: data.company,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        position: data.position,
        domains: Object.keys(data.domains).filter(
          (k) => data.domains[k] === true
        ),
        // message: message,
        topics: Object.keys(data.topics).filter((k) => data.topics[k] === true),
      },
    },
    { withCredentials: true }
  )
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data?.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const getAllUsers = async () => {
  return await axios
    .get("/neoUsers", {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const addUser = async (data) => {
  return await axios("/neoUsers/", {
    method: "POST",
    data,
    headers: {
      ...getAuthHeaders(),
    },
  })
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const updateUser = async (id, data) => {
  if (!id || !data) {
    // show in the alret
    return {
      success: false,
      msg: "No user id or data presented",
    };
  }
  return await axios("/neoUsers/" + id, {
    method: "PUT",
    data,
    headers: {
      ...getAuthHeaders(),
    },
  })
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

// Shared call for the archive/restore endpoints: always resolves
// { success, content? , msg? } and never throws.
const adminRequest = (method, url, data) =>
  axios({ method, url, data, headers: { ...getAuthHeaders() } })
    .then((response) =>
      response.data?.success ? response.data : { success: false, msg: response.data?.msg }
    )
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
        // "forbidden", "seats", ... so a screen can say what to do next.
        reason: err?.response?.data?.reason,
        status: err?.response?.status,
      };
    });

export const archiveUser = async (id) => {
  if (!id) return { success: false, msg: "No user id provided" };
  return adminRequest("post", `/neoUsers/${encodeURIComponent(id)}/archive`);
};

export const restoreUser = async (id) =>
  adminRequest("post", `/neoUsers/${encodeURIComponent(id)}/restore`);

export const getArchivedUsers = async () => adminRequest("get", "/neoUsers/archived");

export const getAllOrganisations = async () => {
  return await axios
    .get("/neoOrganisations", { headers: { ...getAuthHeaders() } })
    .then((response) => {
      if (response.data?.success) return response.data;
      return { success: false, msg: response.data.msg };
    })
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const getOrganisationById = async (id) => {
  return await axios
    .get("/neoOrganisations/" + encodeURIComponent(id), { headers: { ...getAuthHeaders() } })
    .then((response) => {
      if (response.data?.success) return response.data;
      return { success: false, msg: response.data.msg };
    })
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const createOrganisation = async (data) => {
  return await axios("/neoOrganisations", {
    method: "POST",
    data,
    headers: { ...getAuthHeaders() },
  })
    .then((response) => {
      if (response.data?.success) return response.data;
      return { success: false, msg: response.data.msg };
    })
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const updateOrganisation = async (id, data) => {
  return await axios("/neoOrganisations/" + encodeURIComponent(id), {
    method: "PUT",
    data,
    headers: { ...getAuthHeaders() },
  })
    .then((response) => {
      if (response.data?.success) return response.data;
      return { success: false, msg: response.data.msg };
    })
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const archiveOrganisation = async (id) =>
  adminRequest("post", `/neoOrganisations/${encodeURIComponent(id)}/archive`);

export const restoreOrganisation = async (id) =>
  adminRequest("post", `/neoOrganisations/${encodeURIComponent(id)}/restore`);

export const getArchivedOrganisations = async () =>
  adminRequest("get", "/neoOrganisations/archived");

export const getArchiveEntries = async (orgId) =>
  adminRequest("get", `/neoOrganisations/${encodeURIComponent(orgId)}/archive-entries`);

export const restoreArchiveEntry = async (entryId) =>
  adminRequest("post", `/neoOrganisations/archive-entries/${encodeURIComponent(entryId)}/restore`);

export const getOrganisationSubscription = async (orgId) =>
  adminRequest("get", `/neoOrganisations/${encodeURIComponent(orgId)}/subscription`);

export const setOrganisationSubscription = async (orgId, data) =>
  adminRequest("put", `/neoOrganisations/${encodeURIComponent(orgId)}/subscription`, data);

export const getPlans = async () => adminRequest("get", "/api/plans");

// Platform settings: integrations and addresses set in the console. Secrets are
// write-only and never come back.
export const getPlatformSettings = async () => adminRequest("get", "/api/platform-settings");

export const savePlatformSetting = async (key, value) =>
  adminRequest("put", `/api/platform-settings/${encodeURIComponent(key)}`, { value });

export const resetPlatformSetting = async (key) => adminRequest("delete", `/api/platform-settings/${encodeURIComponent(key)}`);

export const testPlatformSettings = async (group) => adminRequest("post", `/api/platform-settings/test/${encodeURIComponent(group)}`);

// ── Database servers (Console → Databases) ─────────────────────────────
// `archived: true` also brings back the databases an admin has archived, for
// the Archived section; the pickers ask for the servers alone.
export const getDatabaseServers = async ({ archived = false } = {}) =>
  adminRequest("get", `/api/database-servers${archived ? "?archived=1" : ""}`);

export const testDatabaseServer = async (input) => adminRequest("post", "/api/database-servers/test", input);

export const createDatabaseServer = async (input) => adminRequest("post", "/api/database-servers", input);

export const updateDatabaseServer = async (id, input) => adminRequest("put", `/api/database-servers/${encodeURIComponent(id)}`, input);

export const archiveDatabaseServer = async (id) => adminRequest("post", `/api/database-servers/${encodeURIComponent(id)}/archive`);

// Archiving a database takes it out of Business Flows and changes nothing in
// Neo4j. `detach` confirms removing it from the workspaces still using it;
// `serverId` and `databaseName` say which database the name stands for, so one
// that was never connected can be archived too.
export const archiveServerDatabase = async (name, { detach = false, serverId = null, databaseName = null } = {}) =>
  adminRequest("post", `/api/database-servers/databases/${encodeURIComponent(name)}/archive`, { detach, serverId, databaseName });

export const restoreServerDatabase = async (name) =>
  adminRequest("post", `/api/database-servers/databases/${encodeURIComponent(name)}/restore`);

export const registerServerDatabase = async (serverId, input) =>
  adminRequest("post", `/api/database-servers/${encodeURIComponent(serverId)}/databases`, input);

export const createServerDatabase = async (serverId, input) =>
  adminRequest("post", `/api/database-servers/${encodeURIComponent(serverId)}/create-database`, input);

// Changes what a plan unlocks, for every tenant on it.
export const savePlanEntitlements = async (planKey, entitlements) =>
  adminRequest("put", `/api/plans/${encodeURIComponent(planKey)}`, { entitlements });

export const revertPlanEntitlements = async (planKey) =>
  adminRequest("delete", `/api/plans/${encodeURIComponent(planKey)}/changes`);

// The services FlowOS currently offers, for the pickers.
export const getFlowOSServices = async () => adminRequest("get", "/api/flowos/services");

// A tenant's own FlowOS grant. Pass null for either value to follow the plan.
export const setOrganisationFlowOS = async (orgId, { services, monthlyCredits }) =>
  adminRequest("put", `/neoOrganisations/${encodeURIComponent(orgId)}/flowos`, { services, monthlyCredits });

// ── RBAC ────────────────────────────────────────────────────────────────
export const setPlatformRole = async (userId, platformRole) =>
  adminRequest("put", `/neoUsers/${encodeURIComponent(userId)}/platform-role`, { platformRole });

export const setDatabaseLabel = async (databaseName, label) =>
  adminRequest("put", `/neoOrganisations/databases/${encodeURIComponent(databaseName)}/label`, { label });

export const setOrganisationMemberRole = async (orgId, userId, role) =>
  adminRequest("put", `/neoOrganisations/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}/role`, { role });

export const getMyWorkspaces = async () => adminRequest("get", "/api/workspace");

export const getWorkspaceMembers = async (orgId) =>
  adminRequest("get", `/api/workspace/${encodeURIComponent(orgId)}/members`);

export const addWorkspaceMember = async (orgId, email, role) =>
  adminRequest("post", `/api/workspace/${encodeURIComponent(orgId)}/members`, { email, role });

export const setWorkspaceMemberRole = async (orgId, userId, role) =>
  adminRequest("put", `/api/workspace/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/role`, { role });

export const removeWorkspaceMember = async (orgId, userId) =>
  adminRequest("delete", `/api/workspace/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`);

// ── Invitations and ownership (Workspace) ─────────────────────────────────
export const getWorkspaceInvitations = async (orgId) => adminRequest("get", `/api/workspace/${encodeURIComponent(orgId)}/invitations`);

export const inviteToWorkspace = async (orgId, email, role) =>
  adminRequest("post", `/api/workspace/${encodeURIComponent(orgId)}/invitations`, { email, role });

export const resendWorkspaceInvitation = async (orgId, id) =>
  adminRequest("post", `/api/workspace/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(id)}/resend`);

export const revokeWorkspaceInvitation = async (orgId, id) =>
  adminRequest("post", `/api/workspace/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(id)}/revoke`);

export const acceptInvitation = async (token) => adminRequest("post", `/api/workspace/invitations/${encodeURIComponent(token)}/accept`);

export const transferOwnership = async (orgId, userId) =>
  adminRequest("post", `/api/workspace/${encodeURIComponent(orgId)}/ownership/transfer`, { userId });

export const stepDownAsOwner = async (orgId) => adminRequest("post", `/api/workspace/${encodeURIComponent(orgId)}/ownership/step-down`);

export const leaveWorkspace = async (orgId) => adminRequest("post", `/api/workspace/${encodeURIComponent(orgId)}/leave`);

// ── Invitations and kind (console) ────────────────────────────────────────
export const getOrganisationInvitations = async (orgId) => adminRequest("get", `/neoOrganisations/${encodeURIComponent(orgId)}/invitations`);

export const inviteToOrganisation = async (orgId, email, role) =>
  adminRequest("post", `/neoOrganisations/${encodeURIComponent(orgId)}/invitations`, { email, role });

export const resendOrganisationInvitation = async (orgId, id) =>
  adminRequest("post", `/neoOrganisations/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(id)}/resend`);

export const revokeOrganisationInvitation = async (orgId, id) =>
  adminRequest("post", `/neoOrganisations/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(id)}/revoke`);

export const setOrganisationKind = async (orgId, kind) => adminRequest("put", `/neoOrganisations/${encodeURIComponent(orgId)}/kind`, { kind });

// ── Invitee (no sign-in) ──────────────────────────────────────────────────
const publicRequest = (method, url, data) =>
  axios({ method, url, data })
    .then((response) => response.data)
    .catch((err) => ({ success: false, status: err?.response?.status, msg: err?.response?.data?.msg || "Couldn't reach Business Flows." }));

export const previewInvitation = async (token) => publicRequest("get", `/neoPublic/invitations/${encodeURIComponent(token)}`);

export const registerFromInvitation = async (token, { firstName, lastName, password }) =>
  publicRequest("post", `/neoPublic/invitations/${encodeURIComponent(token)}/register`, { firstName, lastName, password });

// For an invitee with an account who can't sign in first (e.g. their own
// trial has ended): the password proves who they are, then sign in as usual.
export const acceptInvitationWithPassword = async (token, password) =>
  publicRequest("post", `/neoPublic/invitations/${encodeURIComponent(token)}/accept-with-password`, { password });

// First-run setup of a new instance (no sign-in).
export const getSetupStatus = async () => publicRequest("get", "/api/setup");

export const runSetup = async (input) => publicRequest("post", "/api/setup", input);

export const assignUserToOrganisation = async (orgId, userId) => {
  return await axios(
    "/neoOrganisations/" +
      encodeURIComponent(orgId) +
      "/users/" +
      encodeURIComponent(userId),
    {
      method: "POST",
      headers: { ...getAuthHeaders() },
    }
  )
    .then((response) => {
      if (response.data?.success) return response.data;
      return { success: false, msg: response.data.msg };
    })
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const removeUserFromOrganisation = async (orgId, userId) => {
  return await axios
    .delete(
      "/neoOrganisations/" +
        encodeURIComponent(orgId) +
        "/users/" +
        encodeURIComponent(userId),
      {
        headers: { ...getAuthHeaders() },
      }
    )
    .then((response) => {
      if (response.data?.success) return response.data;
      return { success: false, msg: response.data.msg };
    })
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const assignDatabaseToOrganisation = async (orgId, databaseName) => {
  return await axios(
    "/neoOrganisations/" +
      encodeURIComponent(orgId) +
      "/databases/" +
      encodeURIComponent(databaseName),
    { method: "POST", headers: { ...getAuthHeaders() } }
  )
    .then((response) => {
      if (response.data?.success) return response.data;
      return { success: false, msg: response.data.msg };
    })
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const removeDatabaseFromOrganisation = async (orgId, databaseName) => {
  return await axios
    .delete(
      "/neoOrganisations/" +
        encodeURIComponent(orgId) +
        "/databases/" +
        encodeURIComponent(databaseName),
      {
        headers: { ...getAuthHeaders() },
      }
    )
    .then((response) => {
      if (response.data?.success) return response.data;
      return { success: false, msg: response.data.msg };
    })
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const resetPassword = async (token) => {
  if (!token) {
    // show in the alret
    return {
      success: false,
      msg: "Failed to reset password please contact Admin.",
    };
  }
  return await axios
    .get("/neoPublic/reset-password?index=" + token, {
      headers: {
        ...getAuthHeaders(),
      },
    })
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data?.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const advanceSearch = async (
  property,
  value,
  skip,
  limit,
  orderBy,
  sort,
  label,
  view
) => {
  return await axios
    .get(
      "/processes/search/" +
        property +
        "/" +
        value +
        "?" +
        "skip=" +
        skip +
        "&" +
        "limit=" +
        limit +
        "&orderBy=" +
        orderBy +
        "&sort=" +
        sort +
        "&labels=" +
        label +
        (view ? "&view=" + encodeURIComponent(view) : ""),
      {
        headers: {
          ...getAuthHeaders(),
           "Content-Type": "application/json",
        },
      }
    )
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        status: response.status,
        reason: response.data?.reason,
        msg: response.data.msg,
      };
    })
    // A database that can't be reached answers 503 with a reason and a
    // message of its own; the screen shows that rather than "no results".
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        status: err?.response?.status,
        reason: err?.response?.data?.reason,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const advanceSearchMyFavorites = async (
  property,
  value,
  skip,
  limit,
  orderBy,
  sort,
  label
) => {
  try {
    const response = await axios.get(
      `/processes/searchMyFavorites/${encodeURIComponent(
        property
      )}/${encodeURIComponent(JSON.stringify(value))}`,
      {
        params: {
          skip,
          limit,
          orderBy,
          sort,
          labels: label,
        },
        headers: {
          ...getAuthHeaders(),
           "Content-Type": "application/json",
        },
      }
    );

    if (response.data?.success) {
      return response.data;
    } else {
      return {
        success: false,
        status: response.status,
        reason: response.data?.reason,
        msg: response.data.msg,
      };
    }
  } catch (err) {
    console.error(err);
    return {
      success: false,
      status: err?.response?.status,
      reason: err?.response?.data?.reason,
      msg: err?.response?.data?.msg || "Internal Server Error",
    };
  }
};

export const getProcessLabels = async () => {
  return await axios
    .get("/processes/getLabels", {
      headers: {
        ...getAuthHeaders(),
         "Content-Type": "application/json",
      },
    })
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const getProcessTypes = async () => {
  return await axios
    .get("/processes/getTypes", {
      headers: {
        ...getAuthHeaders(),
         "Content-Type": "application/json",
      },
    })
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const getSpDocumentsByURL = async (spLink) => {
  if (!spLink) return [];
  return await axios
    .get(`/sp/getDocByUrl/${spLink}`, {
      // responseType: 'blob',
      headers: {
        // Accept: "application/json"
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getSpDocumentsByID = async (spID) => {
  if (!spID) return [];
  return await axios
    .get(`/sp/getDocById/${spID}`)
    .then((response) => {
      if (response.status === 200) {
        var base64str = response.data;
        //convert base64 to blob
        var binary = atob(base64str.replace(/\s/g, ""));
        var len = binary.length;
        var buffer = new ArrayBuffer(len);
        var raw = new Uint8Array(buffer);
        for (var i = 0; i < len; i++) {
          raw[i] = binary.charCodeAt(i);
        }
        return raw;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getProcess = async (guid) => {
  if (!guid) return null;
  return await axios
    .get(`/processes/${guid}`, {
      headers: {
        ...getAuthHeaders(),
         "Content-Type": "application/json",
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const getAttributeMap = async () => {
  return await axios
    .get(`/api/admin/attributeMap`, {
      headers: {
        ...getAuthHeaders(),
         "Content-Type": "application/json",
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data;
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

// An object with no id at all: there is nothing to ask for, and the caller
// needs an answer it can show rather than a request for "undefined".
export const getProcessAttributesByPartialName = async (name) => {
  if (!name) return { success: false, content: null, status: 404, msg: "This object has no id, so its details can't be read." };
  return await axios
    .get(`/processes/attributes/${name}`, {
      headers: {
        ...getAuthHeaders(),
         "Content-Type": "application/json",
      },
    })
    .then((response) => {
      if (response.status === 200) {
        return response?.data;
      }
      return { success: false, content: null };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        content: null,
        status: err?.response?.status,
        reason: err?.response?.data?.reason,
        msg: err?.response?.data?.msg || "Couldn't read this object.",
      };
    });
};

export const getAdvanceSearchFilter = async () => {
  return await axios
    .get("/admin/advanceSearchFilter", {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      }
    })
    .then((response) => {
      if (response.data?.success) {
        return Array.isArray(response.data.content) ? response.data.content : [];
      }
      return [];
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const updateAdvanceSearchFilter = async (input) => {
  return await axios
    .post("/api/admin/advanceSearchFilter", [...input], {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      }
    })
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

// Published release notes — visible to all authenticated users
// What's new. Read from /api/release-notes, which asks only for a valid
// token: the notes are about the product, so someone with no database - or
// whose group has no view yet - must still be able to read them. An error
// answers null, so the page can say it couldn't load them rather than claim
// nothing has been published.
export const getReleaseNotes = async () => {
  return await axios
    .get("/api/release-notes", {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    })
    .then((response) => (response.data?.success ? response.data.content : null))
    .catch((err) => { console.error(err); return null; });
};

// Admin: all release notes including drafts
export const getAdminReleaseNotes = async () => {
  return await axios
    .get("/api/admin/releaseNotes", {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    })
    .then((response) => (response.data?.success ? response.data.content : []))
    .catch((err) => { console.error(err); return []; });
};

// Admin: create or update a release note (upsert by version)
export const saveReleaseNote = async (data) => {
  return await axios
    .post("/api/admin/releaseNotes", data, {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    })
    .then((response) => response.data)
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const archiveReleaseNote = async (version) =>
  adminRequest("post", `/api/admin/releaseNotes/${encodeURIComponent(version)}/archive`);

export const restoreReleaseNote = async (version) =>
  adminRequest("post", `/api/admin/releaseNotes/${encodeURIComponent(version)}/restore`);

export const getArchivedReleaseNotes = async () =>
  adminRequest("get", "/api/admin/releaseNotes/archived");

export const initAdvanceSearchFilter = async () => {
  return await axios
    .post(
      "/api/admin/advanceSearchFilter?init=true",
      {},
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      }
    )
    .then((response) => {
      if (response.data?.success) {
        return response.data.content;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const connectDb = async () => {
  return await axios
    .post("/neoPublic/connect-aura-db", {}, {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    })
    .then((response) => response.data)
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

export const connectInitialDb = async () => {
  return await axios
    .post("/neoPublic/connect-initial-db", {}, {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    })
    .then((response) => response.data)
    .catch((err) => {
      console.error(err);
      return { success: false, msg: err?.response?.data?.msg || "Internal Server Error" };
    });
};

// Where this database's explorer starts, and - when there is no answer - why.
// A database that can't be reached must not look like a database with no
// starting point: the first hides the Relationship Explorer, the second is
// something an admin fixes under Views.
// { success, guid } or { success: false, status, reason, msg }.
export const getExplorerStart = async () => {
  return await axios
    .get("/admin/rootguid", {
      headers: {
        ...getAuthHeaders(),
         "Content-Type": "application/json",
      },
    })
    .then((response) => {
      const guid = response.data?.success ? response.data.content : null;
      return { success: true, guid: typeof guid === "string" && guid ? guid : null };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        guid: null,
        status: err?.response?.status,
        reason: err?.response?.data?.reason,
        msg: err?.response?.data?.msg || "Couldn't read where this database starts.",
      };
    });
};

export const getRootGUID = async () => (await getExplorerStart()).guid;

export const updateMyFavorites = async (guid) => {
  return await axios
    .post(
      `/processes/myFavorites/${guid}`,
      {},
      {
        headers: {
          ...getAuthHeaders(),
           "Content-Type": "application/json",
        },
      }
    )
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const removeMyFavorites = async (guid) => {
  return await axios
    .post(
      `/processes/removeMyFavorites/${guid}`,
      {},
      {
       headers: {
        ...getAuthHeaders(),
         "Content-Type": "application/json",
      },
      }
    )
    .then((response) => {
      if (response.data?.success) {
        return response.data;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

export const getMyFavorites = async () => {
  return await axios
    .get("/admin/getMyFavorites", {
      headers: {
        ...getAuthHeaders(),
         "Content-Type": "application/json",
      },
    })
    .then((response) => {
      if (response.data?.success) {
        return response.data.content;
      }
      return {
        success: false,
        msg: response.data.msg,
      };
    })
    .catch((err) => {
      console.error(err);
      return {
        success: false,
        msg: err?.response?.data?.msg || "Internal Server Error",
      };
    });
};

// Console → Features. The answer is the whole picture: { flags, envBlocked,
// defaults, features }, where `features` is the server's own list of switches
// with what each one hides — so the screen never keeps a second copy of it.
const NO_FEATURES = { flags: {}, envBlocked: {}, defaults: {}, features: [] };

export const getAdminFeatureFlags = async () => {
  return await axios
    .get("/api/admin/features", { headers: { ...getAuthHeaders() } })
    .then((r) => (r.data?.success ? { ...NO_FEATURES, ...r.data.content } : NO_FEATURES))
    .catch(() => NO_FEATURES);
};

export const setAdminFeatureFlag = async (key, value) => {
  return await axios
    .post("/api/admin/features", { key, value }, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } })
    .then((r) => r.data)
    // A feature the server's environment blocks answers 409 with its own
    // wording; the screen shows that rather than "something went wrong".
    .catch((err) => ({
      success: false,
      reason: err?.response?.data?.reason,
      msg: err?.response?.data?.msg || "That couldn't be saved. Try again.",
    }));
};

// ── Dynamic diagrams (independent of the static hierarchy) ─────────────────

const diagramRequest = (method, url, data) =>
  axios({ method, url, data, headers: { ...getAuthHeaders(), "Content-Type": "application/json" } })
    .then((response) => {
      // An object opened by its fallback id may have been given a real one on
      // the way in (X-Object-Id, exposed by the CORS policy). The viewer uses
      // it to replace the link with the one worth keeping.
      const objectId = response.headers?.["x-object-id"];
      const body = response.data;
      return objectId && body && typeof body === "object" && !Array.isArray(body) ? { ...body, objectId } : body;
    })
    .catch((err) => ({
      success: false,
      status: err?.response?.status,
      reason: err?.response?.data?.reason,
      // The request's own reference, so the viewer can print it on an error
      // card the way the error boundary does: the body carries it, and the
      // header does too when the answer never reached a route.
      reference: err?.response?.data?.reference || err?.response?.headers?.["x-request-id"] || null,
      msg: err?.response?.data?.msg || "Couldn't load the diagram.",
    }));

// { success, content: { guid, name, type, description, formats: ["bpmn", "svg", ...] } }
export const getDiagramInfo = (guid) => diagramRequest("get", `/processes/diagrams/${encodeURIComponent(guid)}`);

// { success, content: { format, name, mimeType, content, url, isImage, presentation } }
//
// `presentation` ("enhanced" | "original") asks for a stored picture in the
// product's own colours and type, or exactly as it was imported. Left out,
// the database's own view setting decides; the answer always says which of
// the two came back.
export const getDiagram = (guid, format, { presentation } = {}) =>
  diagramRequest(
    "get",
    `/processes/diagrams/${encodeURIComponent(guid)}/${encodeURIComponent(format)}${
      presentation ? `?presentation=${encodeURIComponent(presentation)}` : ""
    }`
  );

// ── Content management (staff) ─────────────────────────────────────────────
// The database is passed explicitly so admins can work on any database
// without changing the one the app is using.
const contentRequest = (method, url, { data, database } = {}) =>
  axios({
    method,
    url,
    data,
    headers: { ...getAuthHeaders(), ...(database ? { database } : {}), "Content-Type": "application/json" },
  })
    .then((response) => response.data)
    .catch((err) => ({
      success: false,
      status: err?.response?.status,
      reason: err?.response?.data?.reason,
      msg: err?.response?.data?.msg || "That didn't work.",
    }));

// ── What users see (view configuration) ────────────────────────────────────
// The resolved configuration of a database: object types, relationships,
// fields per view and view settings. Readable by every signed-in user.
export const getViewConfig = (database) => contentRequest("get", "/processes/view-config", { database });

// Console -> Display: how one database looks to everyone who reads it (staff
// read; content team and the database's workspace admins change it).
const viewsPath = (...parts) => `/processes/settings/views${parts.map((part) => `/${encodeURIComponent(part)}`).join("")}`;

export const getViewsOverview = (database) => contentRequest("get", viewsPath(), { database });

export const getViewFieldOptions = (database, view, objectType) => contentRequest("get", viewsPath("fields", view, objectType), { database });

// "I just made that type - where is it?" Types and relationships are
// discovered with two full scans and kept for five minutes; these read the
// database again now. Refused with 429 if asked twice within ten seconds.
export const refreshViewsCatalogue = (database) => contentRequest("post", viewsPath("refresh"), { database });

export const refreshContentCatalogue = (database) => contentRequest("post", "/processes/content/refresh", { database });

export const saveViewType = (database, objectType, changes) =>
  contentRequest("put", viewsPath("types", objectType), { database, data: changes });

// One switch for a list of types at once - a whole column of the Object types
// tab, in one request rather than one per type.
export const setViewTypeFlag = (database, flag, value, types) =>
  contentRequest("put", viewsPath("flags", flag), { database, data: { value, types } });

export const addViewType = (database, body) => contentRequest("post", viewsPath("types"), { database, data: body });

export const reorderViewTypes = (database, order) => contentRequest("put", viewsPath("order"), { database, data: { order } });

export const resetViewType = (database, objectType) => contentRequest("delete", viewsPath("types", objectType), { database });

export const saveViewRelationship = (database, type, changes) =>
  contentRequest("put", viewsPath("relationships", type), { database, data: changes });

export const resetViewRelationship = (database, type) => contentRequest("delete", viewsPath("relationships", type), { database });

export const saveViewFields = (database, view, objectType, attributes) =>
  contentRequest("put", viewsPath("fields", view, objectType), { database, data: { attributes } });

export const resetViewFields = (database, view, objectType) => contentRequest("delete", viewsPath("fields", view, objectType), { database });

export const saveViewSetting = (database, key, value) => contentRequest("put", viewsPath("settings", key), { database, data: { value } });

export const resetViewSetting = (database, key) => contentRequest("delete", viewsPath("settings", key), { database });

export const listContentObjects = (database, { query, type, limit } = {}) => {
  const params = new URLSearchParams();
  if (query) params.set("query", query);
  if (type) params.set("type", type);
  if (limit) params.set("limit", limit);
  return contentRequest("get", `/processes/diagrams/objects/list?${params.toString()}`, { database });
};

export const listContentObjectTypes = (database) => contentRequest("get", "/processes/diagrams/objects/types", { database });

export const getContentObject = (database, guid) =>
  contentRequest("get", `/processes/content/objects/${encodeURIComponent(guid)}`, { database });

export const createContentObject = (database, body) => contentRequest("post", "/processes/content/objects", { database, data: body });

export const updateContentObject = (database, guid, fields) =>
  contentRequest("put", `/processes/content/objects/${encodeURIComponent(guid)}`, { database, data: fields });

export const unlinkContentObject = (database, guid, parentGuid) =>
  contentRequest("post", `/processes/content/objects/${encodeURIComponent(guid)}/unlink/${encodeURIComponent(parentGuid)}`, { database });

export const listUnlinkedContent = (database, guid) =>
  contentRequest("get", `/processes/content/unlinked${guid ? `?guid=${encodeURIComponent(guid)}` : ""}`, { database });

export const restoreContentLink = (database, entryId) =>
  contentRequest("post", `/processes/content/unlinked/${encodeURIComponent(entryId)}/restore`, { database });

// The attribute names this type's objects already carry, so the editor offers
// one spelling per database. Editing, not configuration.
export const listUsedAttributes = (database, type) =>
  contentRequest("get", `/processes/content/attributes/${encodeURIComponent(type)}`, { database });

export const listDiagramVersions = (database, guid) =>
  contentRequest("get", `/processes/diagrams/${encodeURIComponent(guid)}/versions`, { database });

export const archiveDiagramVersion = (database, guid, diagramId) =>
  contentRequest("post", `/processes/diagrams/${encodeURIComponent(guid)}/versions/${encodeURIComponent(diagramId)}/archive`, { database });

export const restoreDiagramVersion = (database, guid, diagramId) =>
  contentRequest("post", `/processes/diagrams/${encodeURIComponent(guid)}/versions/${encodeURIComponent(diagramId)}/restore`, { database });

// A diagram or any other file: fileName and mimeType travel with it, so a
// file the app doesn't draw downloads under its own name and type.
export const attachDiagramTo = (database, guid, format, content, name, { fileName, mimeType } = {}) =>
  contentRequest("post", `/processes/diagrams/${encodeURIComponent(guid)}`, {
    database,
    data: { format, content, name, fileName: fileName || name, mimeType: mimeType || undefined },
  });

// The current file of one format, as a Blob to save (never opened in a tab).
export const downloadDiagramFile = (database, guid, format) =>
  axios({
    method: "get",
    url: `/processes/diagrams/${encodeURIComponent(guid)}/${encodeURIComponent(format)}/file`,
    responseType: "blob",
    headers: { ...getAuthHeaders(), ...(database ? { database } : {}) },
  })
    .then((response) => {
      const disposition = response.headers?.["content-disposition"] || "";
      const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plain = disposition.match(/filename="([^"]*)"/i);
      let fileName = null;
      try {
        fileName = encoded ? decodeURIComponent(encoded[1]) : plain ? plain[1] : null;
      } catch (e) {
        fileName = plain ? plain[1] : null;
      }
      return { success: true, blob: response.data, fileName };
    })
    .catch((err) => ({ success: false, status: err?.response?.status, msg: "Couldn't download the file." }));

// Our own drawing of an object, as a file. The same picture the map shows,
// written by the server from the same layout (backend/services/generatedMap.js),
// so what a reader downloads is what they were looking at.
export const downloadGeneratedMap = (guid) =>
  axios({
    method: "get",
    url: `/processes/diagrams/${encodeURIComponent(guid)}/map.svg?download=1`,
    responseType: "blob",
    headers: getAuthHeaders(),
  })
    .then((response) => ({ success: true, blob: response.data }))
    .catch((err) => ({ success: false, status: err?.response?.status, msg: "Couldn't download this drawing." }));

// What the create form offers: the database's types and relationships, and
// the relationship to suggest for a type under a parent.
export const getContentChoices = (database, { parentGuid, type } = {}) => {
  const params = new URLSearchParams();
  if (parentGuid) params.set("parentGuid", parentGuid);
  if (type) params.set("type", type);
  const query = params.toString();
  return contentRequest("get", `/processes/content/choices${query ? `?${query}` : ""}`, { database });
};

// Per-database configuration: the properties panel, advanced search and the
// relationship explorer. Every call names the database it applies to.
export const listConfigTypes = (database) => contentRequest("get", "/processes/settings/types", { database });

export const listAttributeMapping = (database) => contentRequest("get", "/processes/settings/attributes", { database });

export const getAttributeMapping = (database, type) =>
  contentRequest("get", `/processes/settings/attributes/${encodeURIComponent(type)}`, { database });

export const saveAttributeMapping = (database, type, attributes) =>
  contentRequest("put", `/processes/settings/attributes/${encodeURIComponent(type)}`, { database, data: { attributes } });

export const removeAttributeMapping = (database, type) =>
  contentRequest("delete", `/processes/settings/attributes/${encodeURIComponent(type)}`, { database });

export const getSearchFilterConfig = (database) => contentRequest("get", "/processes/settings/search-filter", { database });

export const saveSearchFilterConfig = (database, types) =>
  contentRequest("put", "/processes/settings/search-filter", { database, data: { types } });

export const getExplorerConfig = (database) => contentRequest("get", "/processes/settings/explorer", { database });

export const setExplorerRootObject = (database, guid) =>
  contentRequest("put", "/processes/settings/explorer", { database, data: { guid } });

export const clearExplorerRootObject = (database) => contentRequest("delete", "/processes/settings/explorer", { database });

// Is a database ready for search, the explorer, diagrams and trial users?
export const getDatabaseHealth = (database) => contentRequest("get", "/processes/settings/health", { database });

// The objects behind one check, to review before fixing.
export const getDatabaseAffected = (database, key) =>
  contentRequest("get", `/processes/settings/health/affected/${encodeURIComponent(key)}`, { database });

// One named fix; fixes only ever add.
export const fixDatabase = (database, action) =>
  contentRequest("post", "/processes/settings/health/fix", { database, data: { action } });

// Every safe fix the database needs, in one go.
export const makeDatabaseReady = (database) => contentRequest("post", "/processes/settings/health/make-ready", { database });

// Attaches a diagram to an object in the current database (content team only).
export const attachDiagram = (guid, format, content, name) =>
  diagramRequest("post", `/processes/diagrams/${encodeURIComponent(guid)}`, { format, content, name });

// { guid: ["bpmn", ...] } for the objects that have diagrams; {} on failure.
export const getDiagramAvailability = async (guids) => {
  const list = Array.from(new Set((guids || []).filter(Boolean)));
  if (list.length === 0) return {};
  const res = await diagramRequest("post", "/processes/diagrams/availability", { guids: list });
  return res?.success && res.content ? res.content : {};
};

// The public API's own description. It takes an API key, never this app's
// token, so no auth headers are sent.
export const getOpenApiDocument = async () =>
  axios
    .get(`${apiBase()}/api/v1/openapi.json`)
    .then((response) => response.data || null)
    .catch((err) => {
      console.error(err);
      return null;
    });

// Answers the developer portal's "does this key work?" by making the
// smallest real call the API has, GET /api/v1/databases, with the key the
// reader typed. The key is a credential of the workspace, not of this
// session: it is sent on this one request and never stored.
//
// Deliberately on an axios instance of its own. The app's global interceptor
// reads a 403 as an expired session, and a plan that doesn't include the API
// answers 403 - which would sign the reader out for typing a valid key.
const API_REFUSALS = {
  401: "This API key isn't valid. Create a new one in Workspace -> API keys.",
  402: "This key has used its requests for the month.",
  403: "This workspace's plan doesn't include the API.",
  404: "That path isn't part of this API.",
  429: "Too many requests this minute. Wait a moment and check again.",
  503: "The database is unreachable right now. Try again shortly.",
};

// A key travels in a request header, and a header may only carry printable
// ASCII. A key pasted out of a mail, a PDF or the example on the page itself
// often brings something else with it - a line break, a non-breaking space,
// a curly quote, the "…" that ends the sample key. The browser then refuses
// to set the header at all and throws before anything is sent, which arrives
// as an error with no `response` on it.
//
// That is what made a mistyped key report "We couldn't reach the API. Check
// your connection": the connection was blamed for a request the browser never
// made. Anything a header can't carry is a key we can already say is wrong,
// so look before spending a request on it.
const SENDABLE_KEY = /^[!-~]+$/;

const MALFORMED_KEY =
  "This key carries a character a key never has - a line break, a space or a curly quote picked up when it was copied. Nothing was sent.";

// Every answer carries a `reason` as well as a status, because a status of 0
// means several different things and only one of them is the network:
//   empty     - nothing was typed
//   malformed - the key can't be sent as a header, so nothing was sent
//   http      - the server answered, and `status` is what it said
//   network   - the request left and nothing came back
export const checkApiKey = async (key) => {
  const token = String(key || "").trim();
  if (!token) return { ok: false, status: 0, reason: "empty", msg: "Paste the key you were shown when you created it." };
  if (!SENDABLE_KEY.test(token)) return { ok: false, status: 0, reason: "malformed", msg: MALFORMED_KEY };
  try {
    const client = axios.create({ baseURL: axios.defaults.baseURL });
    const response = await client.get(`${apiBase()}/api/v1/databases`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { ok: true, status: 200, reason: "ok", databases: Array.isArray(response.data) ? response.data : [] };
  } catch (err) {
    const status = Number(err?.response?.status) || 0;
    if (status) {
      return {
        ok: false,
        status,
        reason: "http",
        msg: err?.response?.data?.msg || API_REFUSALS[status] || "The server refused this key.",
      };
    }
    // No response. axios only attaches `request` once the call is on its way,
    // so an error without one never left this browser and is not the network.
    if (!err?.request) return { ok: false, status: 0, reason: "malformed", msg: MALFORMED_KEY };
    return {
      ok: false,
      status: 0,
      reason: "network",
      msg: "We couldn't reach the API just now. Check your connection and try again.",
    };
  }
};

// ── Workspaces, people and groups (/api/workspaces, /api/users) ───────────
const ws = (id) => `/api/workspaces/${encodeURIComponent(id)}`;
const part = (value) => encodeURIComponent(value);

export const workspacesApi = {
  list: (archived = false) => adminRequest("get", `/api/workspaces${archived ? "?archived=1" : ""}`),
  create: (name) => adminRequest("post", "/api/workspaces", { name }),
  get: (id) => adminRequest("get", ws(id)),
  rename: (id, name) => adminRequest("put", ws(id), { name }),
  archive: (id) => adminRequest("post", `${ws(id)}/archive`),
  restore: (id) => adminRequest("post", `${ws(id)}/restore`),
  users: (id) => adminRequest("get", `${ws(id)}/users`),
  // What one person actually sees: their databases, the view that applies to
  // each and the group that gives it. Read-only.
  userAccess: (id, userId) => adminRequest("get", `${ws(id)}/users/${part(userId)}/access`),
  setRole: (id, userId, role) => adminRequest("put", `${ws(id)}/users/${part(userId)}`, { role }),
  archiveUser: (id, userId) => adminRequest("post", `${ws(id)}/users/${part(userId)}/archive`),
  keepOnly: (id, userId) => adminRequest("post", `${ws(id)}/users/${part(userId)}/keep-only`),
  // Memberships that were archived (removed, left or moved), and bringing one back. bpExperts only.
  archivedMembers: (id) => adminRequest("get", `${ws(id)}/archived-members`),
  restoreMember: (id, entryId) => adminRequest("post", `${ws(id)}/archived-members/${part(entryId)}/restore`),
  invite: (id, { email, role, groupIds, viewIds }) => adminRequest("post", `${ws(id)}/invitations`, { email, role, groupIds, viewIds }),
  resendInvite: (id, invId) => adminRequest("post", `${ws(id)}/invitations/${part(invId)}/resend`),
  revokeInvite: (id, invId) => adminRequest("post", `${ws(id)}/invitations/${part(invId)}/revoke`),
  groups: (id) => adminRequest("get", `${ws(id)}/groups`),
  createGroup: (id, name) => adminRequest("post", `${ws(id)}/groups`, { name }),
  // The workspace's view library: which object types and attributes a group sees.
  views: (id, archived = false) => adminRequest("get", `${ws(id)}/views${archived ? "?archived=1" : ""}`),
  // With a database, the view also says what it doesn't list yet (newTypes)
  // and what it lists that the database no longer has (missingTypes).
  view: (id, viewId, database) => adminRequest("get", `${ws(id)}/views/${part(viewId)}${database ? `?database=${part(database)}` : ""}`),
  viewCatalogue: (id, database) => adminRequest("get", `${ws(id)}/views/catalogue?database=${part(database)}`),
  // Every view of the workspace against one database, in one call.
  viewsStale: (id, database) => adminRequest("get", `${ws(id)}/views/stale?database=${part(database)}`),
  // { name, definition } (empty, or a file's own text) or { name, fromDatabase }.
  createView: (id, payload) => adminRequest("post", `${ws(id)}/views`, payload),
  saveView: (id, viewId, payload) => adminRequest("put", `${ws(id)}/views/${part(viewId)}`, payload),
  // Copying the built-in "All objects" view needs the database to read
  // today's object types from; a stored view copies its own list.
  copyView: (id, viewId, name, database) =>
    adminRequest("post", `${ws(id)}/views/${part(viewId)}/copy`, { ...(name ? { name } : {}), ...(database ? { database } : {}) }),
  archiveView: (id, viewId) => adminRequest("post", `${ws(id)}/views/${part(viewId)}/archive`),
  restoreView: (id, viewId) => adminRequest("post", `${ws(id)}/views/${part(viewId)}/restore`),
  // "All objects in <database>": the library entry made usable for one
  // database, as an ordinary view people can be given.
  useEverything: (id, database, name) => adminRequest("post", `${ws(id)}/views/use-everything`, { database, ...(name ? { name } : {}) }),
  // Who holds a view - which is how access is given: a view carries the one
  // database it is for, so giving someone a view gives them that database.
  viewPeople: (id, viewId) => adminRequest("get", `${ws(id)}/views/${part(viewId)}/people`),
  setViewPeople: (id, viewId, userIds, mode = "view") => adminRequest("put", `${ws(id)}/views/${part(viewId)}/people`, { userIds, mode }),
  giveView: (id, viewId, userId, mode = "view") => adminRequest("post", `${ws(id)}/views/${part(viewId)}/people/${part(userId)}`, { mode }),
  takeView: (id, viewId, userId) => adminRequest("delete", `${ws(id)}/views/${part(viewId)}/people/${part(userId)}`),
  // The one-off carry-over from the groups that used to decide access: what
  // it would do, then doing it. bpExperts only, and never on a deploy.
  carryOverPreview: (id) => adminRequest("get", `${ws(id)}/carry-over`),
  carryOver: (id) => adminRequest("post", `${ws(id)}/carry-over`),
  saveGroup: (id, groupId, payload) => adminRequest("put", `${ws(id)}/groups/${part(groupId)}`, payload),
  archiveGroup: (id, groupId) => adminRequest("post", `${ws(id)}/groups/${part(groupId)}/archive`),
  restoreGroup: (id, groupId) => adminRequest("post", `${ws(id)}/groups/${part(groupId)}/restore`),
  addDatabase: (id, name) => adminRequest("post", `${ws(id)}/databases/${part(name)}`),
  removeDatabase: (id, name) => adminRequest("post", `${ws(id)}/databases/${part(name)}/remove`),
  savePlan: (id, plan) => adminRequest("put", `${ws(id)}/plan`, plan),
  // A key reads this workspace's databases through /api/v1. The key itself
  // comes back once, from createApiKey, and is never readable again.
  apiKeys: (id) => adminRequest("get", `${ws(id)}/api-keys`),
  createApiKey: (id, name) => adminRequest("post", `${ws(id)}/api-keys`, { name }),
  revokeApiKey: (id, keyId) => adminRequest("post", `${ws(id)}/api-keys/${part(keyId)}/revoke`),
  leave: (id) => adminRequest("post", "/api/workspaces/leave", id ? { id } : {}),
};

// Who changed what, and when. The records are written by the backend's
// securityLog as things happen; these only read them back. Staff read the
// whole instance; a workspace's own admins read their workspace through the
// workspace address, which is scoped by the server, not by anything sent here.
const activityQuery = ({ q = "", category = "", workspace = "", database = "", outcome = "", from = "", to = "", limit, cursor } = {}) =>
  Object.entries({ q, category, workspace, database, outcome, from, to, limit, cursor })
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${part(key)}=${part(value)}`)
    .join("&");

export const activityApi = {
  list: (filters) => adminRequest("get", `/api/activity${activityQuery(filters) ? `?${activityQuery(filters)}` : ""}`),
  options: () => adminRequest("get", "/api/activity/options"),
  workspaceList: (id, filters) =>
    adminRequest("get", `${ws(id)}/activity${activityQuery(filters) ? `?${activityQuery(filters)}` : ""}`),
  workspaceOptions: (id) => adminRequest("get", `${ws(id)}/activity/options`),
};

export const usersApi = {
  list: ({ q = "", filter = "" } = {}) =>
    adminRequest("get", `/api/users?q=${encodeURIComponent(q)}&filter=${encodeURIComponent(filter)}`),
  setStaffRole: (id, role) => adminRequest("put", `/api/users/${encodeURIComponent(id)}/staff-role`, { role }),
};
